import { useEffect, useRef } from 'react';

import { supabase } from '@/lib/supabase';

const PING_INTERVAL_MS = 15_000; // accumulate 15s while visible+active
const FLUSH_INTERVAL_MS = 60_000; // send batched payload every 60s
const IDLE_TIMEOUT_MS = 60_000; // pause counter after 60s of no activity

/**
 * Privacy-respecting reading-time tracker. Counts only seconds the tab is
 * visible, focused, and the user is active. Idle time is excluded entirely
 * (not tracked). Batches to the track-time-spent Edge Function every 60s and
 * flushes the remainder on unload via sendBeacon. No fingerprinting, no
 * behavioural data — just seconds.
 */
export function useTimeTracking(contentId: string | undefined, enabled: boolean) {
  const accumulatedRef = useRef(0);
  const lastActivityRef = useRef(Date.now());
  const pingRef = useRef<number | null>(null);
  const flushRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled || !contentId) return;

    const markActivity = () => {
      lastActivityRef.current = Date.now();
    };
    window.addEventListener('scroll', markActivity, { passive: true });
    window.addEventListener('mousemove', markActivity, { passive: true });
    window.addEventListener('keydown', markActivity);

    pingRef.current = window.setInterval(() => {
      const visible = document.visibilityState === 'visible' && document.hasFocus();
      const active = Date.now() - lastActivityRef.current < IDLE_TIMEOUT_MS;
      if (visible && active) {
        accumulatedRef.current += PING_INTERVAL_MS / 1000;
      }
    }, PING_INTERVAL_MS);

    const flush = async (useBeacon = false) => {
      const seconds = Math.floor(accumulatedRef.current);
      if (seconds < 1) return;
      accumulatedRef.current = 0;
      const url = `${import.meta.env.PUBLIC_SUPABASE_URL}/functions/v1/track-time-spent`;
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) return;
      const payload = JSON.stringify({
        content_id: contentId,
        seconds: Math.min(3600, seconds),
      });
      if (useBeacon && navigator.sendBeacon) {
        // sendBeacon can't set Authorization; use a blob with the token in the
        // body for the Edge Function to read on unload. The function also
        // accepts the bearer header for the normal path.
        navigator.sendBeacon(
          `${url}?beacon=1`,
          new Blob([JSON.stringify({ content_id: contentId, seconds, token })], {
            type: 'application/json',
          }),
        );
        return;
      }
      await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: payload,
        keepalive: true,
      }).catch(() => {});
    };

    flushRef.current = window.setInterval(() => void flush(false), FLUSH_INTERVAL_MS);

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') void flush(false);
    };
    const onUnload = () => void flush(true);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onUnload);

    return () => {
      window.removeEventListener('scroll', markActivity);
      window.removeEventListener('mousemove', markActivity);
      window.removeEventListener('keydown', markActivity);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onUnload);
      if (pingRef.current) window.clearInterval(pingRef.current);
      if (flushRef.current) window.clearInterval(flushRef.current);
      void flush(false);
    };
  }, [contentId, enabled]);
}
